import React, {
	ChangeEventHandler,
	CSSProperties,
	forwardRef,
	HTMLAttributes,
	useContext,
} from "react";
import clsx from "clsx";
import { UseFormRegister } from "react-hook-form";

import { ApplicationEntryProgressContext } from "components/contexts";

import styles from "./switch.module.scss";

type GenericSwitchProps = {
	enabled?: boolean;
	value?: boolean;
	onChange?: ChangeEventHandler<HTMLInputElement>;
	onClick?: any;
	name: string;
	id?: string;
	input?: any;
	inputProps?: HTMLAttributes<HTMLInputElement>;
	style?: CSSProperties;
	isDisabled?: boolean;
	isReadOnly?: boolean;
	register?: UseFormRegister<any>;
};

/**
 * @example // With redux-form
 * <Field component={GenericSwitch} type="checkbox" />
 * @example // With react-hook-form
 * <GenericSwitch register={register} />
 * @example // Controlled component
 * <GenericSwitch enabled={enabled} onChange={handleChange} />
 */
const GenericSwitch = forwardRef<HTMLInputElement, GenericSwitchProps>(
	(
		{
			input,
			enabled,
			onChange,
			name,
			id = name,
			inputProps,
			style,
			onClick,
			isDisabled,
			isReadOnly,
			register,
		},
		ref,
	) => {
		const {
			onChange: formOnChange,
			ref: refRegister,
			...rest
		} = {
			...(register ? register(name) : {}),
		};

		const { triggerSave } = useContext(ApplicationEntryProgressContext);

		const onChangeHandler = event => {
			formOnChange?.(event);
			input?.onChange(event);
			onChange?.(event);
			triggerSave?.();
		};

		const onClickHandler = event => onClick?.(event);

		return (
			<label
				className={clsx(
					styles.track,
					isDisabled && styles.isDisabled,
					isReadOnly && styles.isReadOnly,
				)}
				onClick={e => e.stopPropagation()}
				style={style}
				data-autotest="switchLabel"
			>
				<input
					{...rest}
					type="checkbox"
					ref={refRegister || ref}
					checked={enabled}
					className={clsx(styles.input, inputProps?.className)}
					name={name}
					id={id}
					{...input}
					{...inputProps}
					onChange={onChangeHandler}
					onClick={onClickHandler}
					disabled={isDisabled}
				/>
				<span className={styles.thumb} data-autotest="switchThumb" />
			</label>
		);
	},
);

export default GenericSwitch;
